import { PanelLeft } from "lucide-react";
import type { RefObject } from "react";

import { type VersionSelectorVersion } from "./types";
import { VersionRowTooltipContent } from "./VersionRowTooltipContent";

import { LinkWithSearch } from "@/components/LinkWithSearch";
import { Button } from "@/components/ui/button";
import { getLabelDotClassName } from "@/components/ui/LabelBadge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface CollapsedVersionRailProps {
  versions: VersionSelectorVersion[];
  dynamicReleaseLabelsRegistry?: Record<number, { name: string; id: number }[]>;
  activeVersionNumber: number | null;
  navigateToVersionLink: (versionNumber: number) => string;
  onExpand: () => void;
  containerRef: RefObject<HTMLDivElement | null>;
  loadingRef: RefObject<HTMLDivElement | null>;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  className?: string;
}

export const CollapsedVersionRail = ({
  versions,
  dynamicReleaseLabelsRegistry = {},
  activeVersionNumber,
  navigateToVersionLink,
  onExpand,
  containerRef,
  loadingRef,
  hasNextPage = false,
  isLoadingMore = false,
  className,
}: CollapsedVersionRailProps) => (
  <div className={cn("flex h-full flex-col rounded-lg border border-gray-200 bg-white", className)}>
    <div className="flex justify-center border-b border-gray-200 px-1 py-1">
      <BasicTooltip content="Show versions">
        <span>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onExpand}>
            <PanelLeft className="h-4 w-4" />
          </Button>
        </span>
      </BasicTooltip>
    </div>

    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto px-1 py-2"
    >
      {versions.map(version => {
        const isActive = activeVersionNumber === version.number;
        const dynamicReleaseLabels =
          typeof version.id === "number" ? dynamicReleaseLabelsRegistry[version.id] || [] : [];
        const hasLabels = version.labels.length > 0 || dynamicReleaseLabels.length > 0;
        const primaryLabel = version.labels[0]?.name ?? dynamicReleaseLabels[0]?.name ?? null;
        const indicatorClassName = primaryLabel
          ? getLabelDotClassName(primaryLabel, "environment")
          : "bg-gray-500";

        return (
          <BasicTooltip
            key={version.id}
            content={
              <VersionRowTooltipContent
                version={version}
                dynamicReleaseLabels={dynamicReleaseLabels}
              />
            }
            variant="white"
            side="right"
            contentClassName="max-w-[260px] rounded-lg border border-gray-200 p-3 shadow-lg"
          >
            <LinkWithSearch
              data-version-number={version.number}
              path={navigateToVersionLink(version.number)}
              className={cn(
                "relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-medium transition-colors",
                isActive ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100",
                version.archived && "opacity-50",
              )}
            >
              {`v${version.number}`}
              {hasLabels && (
                <span
                  className={cn(
                    "absolute right-1 top-1 h-1.5 w-1.5 rounded-full ring-1 ring-white",
                    indicatorClassName,
                  )}
                  aria-label="Version has labels"
                  title="Version has labels"
                />
              )}
            </LinkWithSearch>
          </BasicTooltip>
        );
      })}
      {(hasNextPage || isLoadingMore) && (
        <div
          ref={loadingRef as React.RefObject<HTMLDivElement>}
          className="flex h-8 w-full shrink-0 items-center justify-center"
        >
          {isLoadingMore && (
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
          )}
        </div>
      )}
    </div>
  </div>
);
